/**
 * API Client - HTTP client for authentication API
 */

import type {
  MagicLinkRequest,
  MagicLinkVerifyRequest,
  TokenRefreshRequest,
  MagicLinkSentResponse,
  AuthTokenResponse,
  AuthUser,
  ApiError as ApiErrorResponse,
} from '@resolve-ai/shared';

import { API_BASE } from './constants';
import { getAccessToken } from './storage';

/**
 * API Error class for HTTP errors
 */
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic fetch wrapper with authentication
 * IMPORTANT: This is the single source of truth for fetchWithAuth
 * Import this from other API files instead of duplicating
 */
export async function fetchWithAuth<T>(
  url: string,
  // eslint-disable-next-line no-undef
  options: RequestInit = {}
): Promise<T> {
  const token = getAccessToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  // Only set Content-Type for requests with a body
  if (options.body) {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = (await response.json()) as ApiErrorResponse;
    throw new ApiError(error.code, error.message, response.status);
  }

  // Handle 204 No Content - return undefined instead of attempting to parse empty body
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

/**
 * Auth API client
 */
export const authApi = {
  /**
   * Request a magic link email
   */
  async sendMagicLink(email: string): Promise<MagicLinkSentResponse> {
    const body: MagicLinkRequest = { email };
    return fetchWithAuth<MagicLinkSentResponse>('/auth/magic-link', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Verify magic link token and get auth tokens
   */
  async verifyMagicLink(token: string): Promise<AuthTokenResponse> {
    const body: MagicLinkVerifyRequest = { token };
    return fetchWithAuth<AuthTokenResponse>('/auth/magic-link/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshTokens(refreshToken: string): Promise<AuthTokenResponse> {
    const body: TokenRefreshRequest = { refreshToken };
    // Don't use fetchWithAuth here since we're refreshing
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = (await response.json()) as ApiErrorResponse;
      throw new ApiError(error.code, error.message, response.status);
    }

    return response.json() as Promise<AuthTokenResponse>;
  },

  /**
   * Exchange an OAuth transfer code for auth tokens
   */
  async exchangeCode(code: string): Promise<AuthTokenResponse> {
    const response = await fetch(`${API_BASE}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const error = (await response.json()) as ApiErrorResponse;
      throw new ApiError(error.code, error.message, response.status);
    }

    return response.json() as Promise<AuthTokenResponse>;
  },

  /**
   * Logout and invalidate session
   */
  async logout(refreshToken: string): Promise<void> {
    await fetchWithAuth<{ message: string }>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<AuthUser> {
    return fetchWithAuth<AuthUser>('/auth/me');
  },
};
